(() => {
  'use strict';

  if (window.__jbWishlistBridgeLoaded) return;
  window.__jbWishlistBridgeLoaded = true;

  const STORAGE_KEY = 'jb_giftreggie_pending_v1';
  const BUTTON_SELECTOR = '.giftreggie-pdp-wishlist-cta';
  const WISHLIST_ROOT = '/apps/giftregistry/wishlist';
  const EXPERIENCE_STYLE_ID = 'jb-wishlist-experience-styles';
  const MAX_AGE_MS = 10 * 60 * 1000;
  const STUCK_TIMEOUT_MS = 4000;
  const REMOVE_TRACKING_TIMEOUT_MS = 12 * 1000;
  const OUTBOX_STORAGE_KEY = 'jb_klaviyo_wishlist_outbox_v1';
  const MAX_OUTBOX_EVENTS = 10;
  const KLAVIYO_CLIENT_EVENT_URL = 'https://a.klaviyo.com/client/events?company_id=PxyTsW';
  const KLAVIYO_REVISION = '2026-07-15';
  const THEME_ID = '192938574194';
  let isFlushingOutbox = false;
  let hasActiveAddAttempt = false;
  let hasActiveRemovalAttempt = false;

  function textValue(value) {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
  }

  function parseJson(value) {
    if (typeof value !== 'string' || !value.trim()) return null;

    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function pageJson(id) {
    const element = document.getElementById(id);
    return element ? parseJson(element.textContent || '') : null;
  }

  function findSelectedVariant(data, variantId) {
    if (Array.isArray(data)) {
      for (const variant of data) {
        const selected = findSelectedVariant(variant, variantId);
        if (selected) return selected;
      }

      return null;
    }

    if (!data || typeof data !== 'object') return null;

    if (data.id !== undefined && (!variantId || textValue(data.id) === variantId)) {
      return data;
    }

    if (data.variant) {
      const variant = findSelectedVariant(data.variant, variantId);
      if (variant) return variant;
    }

    if (data.variants) return findSelectedVariant(data.variants, variantId);

    return null;
  }

  function absoluteWishlistUrl() {
    try {
      return new URL(WISHLIST_ROOT, window.location.origin).href;
    } catch (error) {
      return '';
    }
  }

  function absoluteProductUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('variant', variantId);
      return url.href;
    } catch (error) {
      return '';
    }
  }

  function normalizeImageUrl(image) {
    const source = textValue(
      image && typeof image === 'object' ? (image.src || image.url) : image
    );

    if (!source) return '';

    try {
      const url = new URL(source.startsWith('//') ? `https:${source}` : source, window.location.origin);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  function selectedProductSnapshot() {
    const variantId = textValue(selectedVariantId());
    if (!variantId) return null;

    const scripts = document.querySelectorAll('script[type="application/json"][data-variant]');
    let variant = null;

    for (const script of scripts) {
      variant = findSelectedVariant(parseJson(script.textContent || ''), variantId);
      if (variant) break;
    }

    if (!variant) return null;

    const giftReggieData = pageJson('gift_reggie_data') || {};
    const selectedId = textValue(variant.id) || variantId;
    const productId = textValue(
      giftReggieData.productId || variant.product_id || variant.productId
    );
    const productName = textValue(variant.product_title || variant.productTitle || variant.name);
    const variantName = textValue(variant.title);
    const priceCents = Number(variant.price);
    const price = Number.isFinite(priceCents) && priceCents >= 0
      ? Number((priceCents / 100).toFixed(2))
      : null;
    const productUrl = absoluteProductUrl(selectedId);

    if (!selectedId || !productId || !productName || !variantName || !productUrl) return null;

    return {
      wishlistUrl: absoluteWishlistUrl(),
      productId,
      productName,
      productUrl,
      variantId: selectedId,
      variantName,
      sku: textValue(variant.sku),
      price,
      imageUrl: normalizeImageUrl(variant.featured_image || variant.featuredImage)
    };
  }

  function createEventId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }

      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        const bytes = window.crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
      }
    } catch (error) {}

    return `jbw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  function createActionRecord(type) {
    try {
      return {
        eventId: createEventId(),
        type,
        createdAt: Date.now(),
        snapshot: selectedProductSnapshot()
      };
    } catch (error) {
      return null;
    }
  }

  // Customer identifiers stay inside the Klaviyo profile payload and are never logged.
  function verifiedCustomerProfile() {
    const giftReggieData = pageJson('gift_reggie_data') || {};
    const accountData = pageJson('shopify-customer-account-data') || {};
    const accountCustomer = accountData.customer || {};
    const email = [
      textValue(giftReggieData.customerEmail),
      textValue(accountCustomer.email)
    ].find((candidate) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate));

    if (!email) return null;

    const profile = { email };
    const externalId = textValue(giftReggieData.customerId || accountCustomer.id);

    if (externalId) profile.external_id = externalId;

    return profile;
  }

  function eventFromAction(action) {
    if (
      !action ||
      !action.eventId ||
      !action.snapshot ||
      (action.type !== 'add' && action.type !== 'remove')
    ) {
      return null;
    }

    const snapshot = action.snapshot;
    const profile = verifiedCustomerProfile();
    const occurredAt = new Date(action.createdAt);

    if (
      !profile ||
      !snapshot.wishlistUrl ||
      !snapshot.productId ||
      !snapshot.productName ||
      !snapshot.productUrl ||
      !snapshot.variantId ||
      !snapshot.variantName ||
      Number.isNaN(occurredAt.getTime())
    ) {
      return null;
    }

    const attributes = {
      metric: {
        data: {
          type: 'metric',
          attributes: {
            name: action.type === 'add' ? 'Wishlist Item Added' : 'Wishlist Item Removed'
          }
        }
      },
      profile: {
        data: {
          type: 'profile',
          attributes: profile
        }
      },
      properties: {
        'Wishlist URL': snapshot.wishlistUrl,
        'Product ID': snapshot.productId,
        'Product Name': snapshot.productName,
        'Product URL': snapshot.productUrl,
        'Variant ID': snapshot.variantId,
        'Variant Name': snapshot.variantName,
        SKU: snapshot.sku,
        Price: snapshot.price,
        Currency: 'USD',
        'Image URL': snapshot.imageUrl,
        Source: 'Gift Reggie Wishlist',
        'Theme ID': THEME_ID
      },
      time: occurredAt.toISOString(),
      unique_id: action.eventId
    };

    if (snapshot.price !== null) {
      attributes.value = snapshot.price;
      attributes.value_currency = 'USD';
    }

    return {
      eventId: action.eventId,
      payload: {
        data: {
          type: 'event',
          attributes
        }
      }
    };
  }

  function readOutbox() {
    try {
      const outbox = JSON.parse(sessionStorage.getItem(OUTBOX_STORAGE_KEY) || '[]');
      return Array.isArray(outbox) ? outbox : null;
    } catch (error) {
      return null;
    }
  }

  function writeOutbox(outbox) {
    try {
      sessionStorage.setItem(OUTBOX_STORAGE_KEY, JSON.stringify(outbox));
      return true;
    } catch (error) {
      return false;
    }
  }

  function queueOutboxEvent(event) {
    const outbox = readOutbox();

    if (!outbox) return false;
    if (outbox.some((entry) => entry && entry.eventId === event.eventId)) return true;
    if (outbox.length >= MAX_OUTBOX_EVENTS) return false;

    outbox.push(event);
    return writeOutbox(outbox);
  }

  function removeOutboxEvent(eventId) {
    const outbox = readOutbox();

    if (!outbox) return false;

    const nextOutbox = outbox.filter((entry) => !entry || entry.eventId !== eventId);
    return nextOutbox.length !== outbox.length && writeOutbox(nextOutbox);
  }

  function validOutboxEvent(event) {
    return Boolean(event && event.eventId && event.payload && event.payload.data);
  }

  function flushKlaviyoOutbox() {
    if (
      isFlushingOutbox ||
      (window.navigator && window.navigator.onLine === false) ||
      typeof window.fetch !== 'function'
    ) {
      return;
    }

    const outbox = readOutbox();
    if (!outbox || !outbox.some(validOutboxEvent)) return;

    isFlushingOutbox = true;
    const skippedEventIds = new Set();

    const sendNext = () => {
      const latestOutbox = readOutbox();
      const event = latestOutbox && latestOutbox.find((entry) => (
        validOutboxEvent(entry) && !skippedEventIds.has(entry.eventId)
      ));

      if (!event) {
        isFlushingOutbox = false;
        return;
      }

      let request;

      try {
        request = window.fetch(KLAVIYO_CLIENT_EVENT_URL, {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(event.payload),
          keepalive: true
        });
      } catch (error) {
        isFlushingOutbox = false;
        return;
      }

      Promise.resolve(request).then((response) => {
        if (!response) {
          isFlushingOutbox = false;
          return;
        }

        if (response.status === 202) {
          if (!removeOutboxEvent(event.eventId)) {
            isFlushingOutbox = false;
            return;
          }

          sendNext();
          return;
        }

        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          skippedEventIds.add(event.eventId);
          sendNext();
          return;
        }

        isFlushingOutbox = false;
      }).catch(() => {
        isFlushingOutbox = false;
      });
    };

    sendNext();
  }

  function queueConfirmedAction(action) {
    try {
      const event = eventFromAction(action);

      if (event && queueOutboxEvent(event)) flushKlaviyoOutbox();
    } catch (error) {}
  }

  function readPending() {
    try {
      const pending = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');

      if (!pending || !pending.createdAt || Date.now() - pending.createdAt > MAX_AGE_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return pending;
    } catch (error) {
      return null;
    }
  }

  function writePending(pending) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
    } catch (error) {
      // The native Gift Reggie flow remains available if storage is blocked.
    }
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Nothing else to clean up.
    }
  }

  function selectedVariantId() {
    const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? input.value : '';
  }

  function buildReturnUrl() {
    const url = new URL(window.location.href);
    const variantId = selectedVariantId();

    url.search = '';
    url.hash = '';

    if (variantId) {
      url.searchParams.set('variant', variantId);
    }

    return url.pathname + url.search;
  }

  function cleanRetryMarker() {
    const url = new URL(window.location.href);

    if (!url.searchParams.has('jb_wishlist_retry')) return;

    url.searchParams.delete('jb_wishlist_retry');
    history.replaceState({}, '', url.pathname + url.search + url.hash);
  }

  function isAdded(button) {
    return /^\s*(?:remove from wishlist|added to wishlist)\s*$/i.test(button.textContent || '');
  }

  function isRemoveState(button) {
    return /^\s*remove from wishlist\s*$/i.test(button.textContent || '');
  }

  function isAddState(button) {
    return /^\s*add to wishlist\s*$/i.test(button.textContent || '');
  }

  function isBusy(button) {
    return Boolean(
      button.disabled ||
      button.getAttribute('aria-busy') === 'true' ||
      button.dataset.jbWishlistLoading === 'true' ||
      /^\s*adding to wishlist(?:…|\.\.\.)?\s*$/i.test(button.textContent || '')
    );
  }

  function hasPendingManualAdd() {
    const pending = readPending();
    return Boolean(pending && pending.stage === 'manual');
  }

  // Preserve the first click's record across list creation so retry uses its same ID and snapshot.
  function actionForPending(pending) {
    const existingAction = pending && pending.action;

    if (
      existingAction &&
      typeof existingAction.eventId === 'string' &&
      existingAction.eventId
    ) {
      if (existingAction.type && existingAction.type !== 'add') return null;

      let repaired = false;

      if (!existingAction.type) {
        existingAction.type = 'add';
        repaired = true;
      }

      if (!existingAction.createdAt && pending.createdAt) {
        existingAction.createdAt = pending.createdAt;
        repaired = true;
      }

      if (!existingAction.snapshot) {
        const snapshot = selectedProductSnapshot();

        if (!snapshot) return null;

        existingAction.snapshot = snapshot;
        repaired = true;
      }

      if (repaired) writePending(pending);

      return existingAction;
    }

    const action = createActionRecord('add');

    if (pending) {
      pending.action = action;
      writePending(pending);
    }

    return action;
  }

  // The rendered Gift Reggie state is the success seam; clicks alone never queue an event.
  function completeAddedAttempt(pending, button) {
    if (!button || !isAdded(button)) return false;

    queueConfirmedAction(actionForPending(pending));
    button.removeAttribute('aria-busy');
    button.removeAttribute('data-jb-wishlist-loading');
    clearPending();
    cleanRetryMarker();
    return true;
  }

  function observeWishlistState(matches, onConfirmed) {
    let confirmed = false;
    let observer;

    const check = () => {
      if (confirmed) return true;

      const button = document.querySelector(BUTTON_SELECTOR);
      if (!button || !matches(button)) return false;

      confirmed = true;
      if (observer) observer.disconnect();

      try {
        onConfirmed(button);
      } catch (error) {}

      return true;
    };

    if (typeof window.MutationObserver === 'function' && document.documentElement) {
      observer = new window.MutationObserver(check);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
      });
    }

    return {
      check,
      stop: () => {
        if (observer) observer.disconnect();
      }
    };
  }

  function isStuck(button) {
    return button.disabled && (
      button.dataset.jbWishlistLoading === 'true' ||
      /^\s*\.{3}\s*$/.test(button.textContent || '')
    );
  }

  function showRecovery(button) {
    if (!button || !button.parentNode) return;

    const replacement = button.cloneNode(false);
    replacement.type = 'button';
    replacement.disabled = false;
    replacement.classList.add('jb-wishlist-recovery');
    replacement.removeAttribute('aria-busy');
    replacement.removeAttribute('data-jb-wishlist-loading');
    replacement.textContent = 'View Your Wishlist';
    replacement.addEventListener('click', () => {
      window.location.assign(WISHLIST_ROOT);
    });

    button.replaceWith(replacement);
    clearPending();
    cleanRetryMarker();
  }

  function monitorAttempt(pending) {
    if (hasActiveAddAttempt) return;

    hasActiveAddAttempt = true;
    const watcher = observeWishlistState(
      isAdded,
      () => queueConfirmedAction(actionForPending(pending))
    );

    watcher.check();

    window.setTimeout(() => {
      watcher.stop();
      hasActiveAddAttempt = false;

      const button = document.querySelector(BUTTON_SELECTOR);

      if (!button) return;

      if (isAdded(button)) {
        completeAddedAttempt(pending, button);
        return;
      }

      if (isStuck(button)) {
        pending.stage = 'creating';
        writePending(pending);
        window.location.assign(WISHLIST_ROOT + '?jb_wishlist_create=1');
        return;
      }

      showRecovery(button);
    }, STUCK_TIMEOUT_MS);
  }

  function monitorRemovalAttempt(action) {
    if (hasActiveRemovalAttempt) return;

    hasActiveRemovalAttempt = true;
    const watcher = observeWishlistState(isAddState, () => {
      queueConfirmedAction(action);
      hasActiveRemovalAttempt = false;
    });

    window.setTimeout(() => watcher.check(), 0);

    window.setTimeout(() => {
      watcher.stop();
      hasActiveRemovalAttempt = false;
    }, REMOVE_TRACKING_TIMEOUT_MS);
  }

  function showAddingFeedback(button) {
    window.setTimeout(() => {
      if (!button.isConnected || isAdded(button)) return;

      button.dataset.jbWishlistLoading = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding to Wishlist…';
    }, 0);
  }

  function waitForElement(selector, callback, timeoutMs = 10000) {
    const existing = document.querySelector(selector);

    if (existing) {
      callback(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);

      if (!element) return;

      observer.disconnect();
      callback(element);
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    window.setTimeout(() => observer.disconnect(), timeoutMs);
  }

  function injectExperienceStyles() {
    if (document.getElementById(EXPERIENCE_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = EXPERIENCE_STYLE_ID;
    style.textContent = `
      .product-info__block-item.jb-wishlist-product-link {
        margin-block-start: -2px;
        text-align: center;
      }

      .jb-wishlist-product-link .giftreggie-pdp-button-area,
      .jb-wishlist-product-link .giftreggie-pdp-cta-area {
        width: auto !important;
        min-height: 0 !important;
        margin: 0 !important;
        align-items: center !important;
        justify-content: center !important;
      }

      .jb-wishlist-product-link .giftreggie-pdp-wishlist-cta {
        width: auto !important;
        min-width: 0 !important;
        min-height: 44px !important;
        border: 0 !important;
        border-radius: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
        color: #5f5752 !important;
        font-size: 11px !important;
        font-weight: 500 !important;
        letter-spacing: .11em !important;
        line-height: 1.3 !important;
        padding: 8px 4px !important;
        text-decoration: none !important;
        text-transform: uppercase !important;
      }

      .jb-wishlist-product-link .giftreggie-pdp-wishlist-cta:not(.giftreggie-pdp-wishlist-cta--in-wishlist):not([data-jb-wishlist-loading="true"]):not(.jb-wishlist-recovery)::before {
        content: '+ ';
      }

      .jb-wishlist-product-link .giftreggie-pdp-wishlist-cta--in-wishlist::before {
        content: '✓ ';
      }

      .jb-wishlist-product-link .giftreggie-pdp-wishlist-cta:hover,
      .jb-wishlist-product-link .giftreggie-pdp-wishlist-cta:focus-visible {
        color: #2f2a27 !important;
        background: transparent !important;
        opacity: .72;
      }

      .jb-wishlist-product-link .giftreggie-pdp-wishlist-cta:disabled {
        cursor: wait !important;
        opacity: .62 !important;
      }

      .jb-wishlist-enhanced {
        width: min(1120px, calc(100% - 48px));
        margin: 64px auto 96px;
      }

      .jb-wishlist-enhanced .head {
        margin: 0;
        color: #36322f;
        font-size: clamp(28px, 3.2vw, 44px);
        font-weight: 400;
        letter-spacing: .08em;
        line-height: 1.15;
        text-align: center;
        text-transform: uppercase;
      }

      .jb-wishlist-intro {
        max-width: 620px;
        margin: 16px auto 44px;
        color: #68615d;
        font-size: 16px;
        line-height: 1.65;
        text-align: center;
      }

      .jb-wishlist-landing-v2 {
        display: flow-root;
      }

      .jb-wishlist-landing-v2 .giftreggie-landing-overview {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 0;
      }

      .jb-wishlist-landing-v2 .giftreggie-landing-overview a {
        display: block;
        border-top: 1px solid #d8d2ce;
        color: #36322f;
        text-decoration: none;
        transition: background-color .2s ease, padding-left .2s ease;
      }

      .jb-wishlist-landing-v2 .giftreggie-landing-overview a:last-child {
        border-bottom: 1px solid #d8d2ce;
      }

      .jb-wishlist-landing-v2 .giftreggie-landing-overview a:hover,
      .jb-wishlist-landing-v2 .giftreggie-landing-overview a:focus-visible {
        background: #fbf4f1;
        padding-left: 12px;
      }

      .jb-wishlist-landing-v2 .giftreggie-landing-row {
        padding: 26px 12px;
      }

      .jb-wishlist-landing-v2 .giftreggie-landing-row h4 {
        margin: 0 0 8px;
        font-size: 18px;
        font-weight: 600;
        letter-spacing: .12em;
        text-transform: uppercase;
      }

      .jb-wishlist-landing-v2 .giftreggie-landing-row p {
        margin: 0;
        color: #68615d;
        font-size: 15px;
        line-height: 1.5;
      }

      .jb-wishlist-landing-v2 .giftreggie-landing-image img {
        display: block;
        width: 100%;
        aspect-ratio: 4 / 3;
        object-fit: cover;
      }

      .jb-wishlist-find-v2 {
        max-width: 860px;
      }

      .jb-wishlist-find-v2 .gift-reggie-search-form {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 28px 34px;
        width: 100%;
        max-width: 720px;
        margin-inline: auto !important;
        padding: 38px;
        border: 1px solid #ded9d5;
        background: #fff;
      }

      .jb-wishlist-find-v2 .jb-wishlist-field {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin: 0;
      }

      .jb-wishlist-find-v2 .jb-wishlist-field label {
        color: #514a46;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .12em;
        text-transform: uppercase;
      }

      .jb-wishlist-find-v2 .jb-wishlist-field input {
        width: 100%;
        min-height: 48px;
        box-sizing: border-box;
        border: 0;
        border-bottom: 1px solid #817973;
        border-radius: 0;
        background: #fbf8f6;
        color: #36322f;
        font: inherit;
        font-size: 16px;
        padding: 10px 12px;
      }

      .jb-wishlist-find-v2 .jb-wishlist-field input:focus {
        border-bottom-color: #36322f;
        outline: 2px solid #d7b6a9;
        outline-offset: 2px;
      }

      .jb-wishlist-find-v2 .bottom-button {
        grid-column: 1 / -1;
        margin: 4px 0 0;
        text-align: center;
      }

      .jb-wishlist-find-v2 .bottom-button > span:first-child {
        display: none;
      }

      .jb-wishlist-find-v2 .bottom-button input[type="submit"] {
        min-width: 230px;
        min-height: 48px;
        border: 1px solid #36322f;
        border-radius: 0;
        background: #36322f;
        color: #fff;
        cursor: pointer;
        font: inherit;
        font-size: 11px;
        letter-spacing: .13em;
        padding: 0 24px;
        text-transform: uppercase;
      }

      .jb-wishlist-find-help {
        margin: 24px auto 0;
        color: #756d68;
        font-size: 13px;
        line-height: 1.55;
        text-align: center;
      }

      .jb-wishlist-find-v2 .giftreggie-footer {
        margin: 28px auto 0;
        border: 0;
        text-align: center;
      }

      .jb-wishlist-find-v2 .giftreggie-footer p {
        margin: 0;
      }

      .jb-wishlist-find-v2 .giftreggie-footer a {
        color: #514a46;
        font-size: 11px;
        font-weight: 500;
        letter-spacing: .11em;
        text-decoration: none;
        text-transform: uppercase;
      }

      .jb-wishlist-find-v2 .giftreggie-footer a::before {
        content: '+ ';
      }

      .jb-wishlist-find-v2 .giftreggie-footer a:hover {
        color: #8f5f50;
        opacity: .72;
      }

      .jb-wishlist-signed-out {
        width: min(960px, calc(100% - 48px));
        margin: 64px auto 96px;
      }

      .jb-wishlist-signed-out > form {
        margin: 0;
      }

      .jb-wishlist-auth {
        display: block;
        border: 1px solid #ded9d5;
        background: #fff;
      }

      .jb-wishlist-auth__copy {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        box-sizing: border-box;
        width: 100%;
        max-width: 760px;
        margin: 0 auto;
        padding: clamp(44px, 6vw, 76px);
      }

      .jb-wishlist-auth__eyebrow {
        margin: 0 0 18px;
        color: #8f5f50;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .18em;
        line-height: 1.3;
        text-transform: uppercase;
      }

      .jb-wishlist-auth__title {
        max-width: 540px;
        margin: 0;
        color: #36322f;
        font-family: var(--heading-font-family, serif);
        font-size: var(--text-h1, 2rem);
        font-weight: 400;
        letter-spacing: .01em;
        line-height: 1.06;
      }

      .jb-wishlist-auth__description {
        max-width: 540px;
        margin: 22px 0 30px;
        color: #68615d;
        font-size: 16px;
        line-height: 1.65;
      }

      .jb-wishlist-auth__primary {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 310px;
        min-height: 52px;
        padding: 12px 24px;
        border: 1px solid #36322f;
        background: #36322f;
        color: #fff;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .14em;
        line-height: 1.3;
        text-align: center;
        text-decoration: none;
        text-transform: uppercase;
        transition: background-color .2s ease, color .2s ease;
      }

      .jb-wishlist-auth__primary:hover,
      .jb-wishlist-auth__primary:focus-visible {
        background: #fff;
        color: #36322f;
      }

      .jb-wishlist-auth__primary:focus-visible,
      .jb-wishlist-auth__find:focus-visible {
        outline: 2px solid #8f5f50;
        outline-offset: 3px;
      }

      .jb-wishlist-auth__note {
        margin: 11px 0 0;
        color: #756d68;
        font-size: 12px;
        line-height: 1.5;
      }

      .jb-wishlist-auth__find {
        margin-top: 34px;
        color: #514a46;
        font-size: 13px;
        line-height: 1.5;
        text-decoration: none;
      }

      .jb-wishlist-auth__find span {
        display: inline-block;
        margin-left: 5px;
        transition: transform .2s ease;
      }

      .jb-wishlist-auth__find:hover span,
      .jb-wishlist-auth__find:focus-visible span {
        transform: translateX(4px);
      }

      .jb-wishlist-manager-v2 .giftreggie-sharing {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 14px;
        margin: 0 0 34px;
      }

      .jb-wishlist-manager-v2 .jb-wishlist-intro {
        margin-bottom: 20px;
      }

      .jb-wishlist-manager-v2 .giftreggie-permalink {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        white-space: nowrap;
      }

      .jb-wishlist-manager-v2 .giftreggie-share-buttons {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px;
        margin: 0;
        padding: 0;
      }

      .jb-wishlist-manager-v2 .giftreggie-share-buttons li,
      .jb-wishlist-copy-link,
      .jb-wishlist-print-button,
      .jb-wishlist-continue {
        display: inline-flex;
        min-height: 40px;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        border: 1px solid #87817d;
        background: transparent;
        color: #36322f;
        cursor: pointer;
        font: inherit;
        font-size: 11px;
        letter-spacing: .12em;
        line-height: 1;
        padding: 0 16px;
        text-decoration: none;
        text-transform: uppercase;
      }

      .jb-wishlist-manager-v2 .giftreggie-share-buttons li:hover,
      .jb-wishlist-copy-link:hover,
      .jb-wishlist-print-button:hover,
      .jb-wishlist-continue:hover {
        border-color: #36322f;
        background: #36322f;
        color: #fff;
      }

      .jb-wishlist-actions {
        display: flex;
        justify-content: center;
        margin: 0 0 42px;
      }

      .jb-wishlist-continue {
        border-color: #36322f;
        background: #36322f;
        color: #fff;
      }

      .jb-wishlist-print-note {
        display: none !important;
      }

      .jb-wishlist-manager-v2 table.giftreggie-view-registry {
        display: none !important;
        width: 100%;
        border: 1px solid #ded9d5;
        border-collapse: collapse;
        background: #fff;
      }

      .jb-wishlist-manager-v2 table.giftreggie-view-registry th {
        border-bottom: 1px solid #ded9d5;
        background: #fbf7f5;
        color: #68615d;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: .14em;
        padding: 14px 16px;
        text-align: left;
      }

      .jb-wishlist-manager-v2 table.giftreggie-view-registry td {
        border-bottom: 1px solid #eeeae7;
        color: #36322f;
        padding: 20px 16px;
        vertical-align: middle;
      }

      .jb-wishlist-manager-v2 table.giftreggie-view-registry td a {
        color: #36322f;
        line-height: 1.5;
        text-decoration-thickness: 1px;
        text-underline-offset: 4px;
      }

      .jb-wishlist-manager-v2 table.giftreggie-view-registry img {
        width: 112px;
        height: 112px;
        object-fit: contain;
      }

      .jb-wishlist-manager-v2 table.giftreggie-view-registry button,
      .jb-wishlist-manager-v2 .giftreggie-add-to-cart {
        min-height: 42px;
        border: 1px solid #36322f;
        background: #36322f;
        color: #fff;
        cursor: pointer;
        font: inherit;
        font-size: 10px;
        letter-spacing: .12em;
        padding: 0 18px;
        text-transform: uppercase;
      }

      .jb-wishlist-manager-v2 .remove-button {
        width: 38px;
        min-width: 38px;
        height: 38px;
        min-height: 38px !important;
        border-color: #aaa29d !important;
        border-radius: 50%;
        background: transparent !important;
        color: #68615d !important;
        font-size: 20px !important;
        padding: 0 !important;
      }

      .jb-wishlist-manager-v2 .action-buttons {
        margin-top: 34px;
        text-align: center;
      }

      .jb-wishlist-manager-v2 #delete-button {
        border: 0;
        border-bottom: 1px solid currentColor;
        background: transparent;
        color: #756d68;
        cursor: pointer;
        font: inherit;
        font-size: 12px;
        padding: 4px 0;
      }

      .jb-wishlist-manager-v2 .giftreggie-mobile {
        display: grid !important;
        grid-template-columns: repeat(auto-fit, minmax(250px, 340px));
        justify-content: center;
        gap: 24px;
        margin: 0 auto;
        padding: 0;
      }

      .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-registry-variant {
        position: relative;
        margin: 0;
        border: 1px solid #ded9d5;
        background: #fff;
        list-style: none;
        padding: 24px;
        text-align: center;
      }

      .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-item-image img {
        display: block;
        width: min(100%, 340px);
        height: auto !important;
        max-height: none !important;
        margin: 0 auto 18px;
        aspect-ratio: 1;
        object-fit: contain;
      }

      .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-item-name {
        display: inline-block;
        margin-bottom: 14px;
        color: #36322f;
        line-height: 1.5;
      }

      .jb-wishlist-manager-v2 .giftreggie-mobile form[action="/cart/add"] {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 18px;
      }

      .jb-wishlist-manager-v2 .giftreggie-registry-variant-delete {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 1;
      }

      .jb-wishlist-search-shortcut {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 18px;
        width: min(100%, 780px);
        box-sizing: border-box;
        margin-inline: auto;
        border: 1px solid #ded9d5;
        background: #fbf8f6;
        color: #36322f;
        padding: 20px 22px;
        text-decoration: none;
      }

      .jb-wishlist-search-shortcut--full {
        margin-block: 4px 10px;
      }

      .jb-wishlist-search-shortcut--predictive {
        width: 100%;
        margin: 22px 0 30px;
      }

      .jb-wishlist-search-shortcut[hidden] {
        display: none !important;
      }

      .jb-wishlist-search-shortcut__heart {
        color: #8f5f50;
        font-family: Georgia, serif;
        font-size: 30px;
        font-weight: 400;
        line-height: 1;
      }

      .jb-wishlist-search-shortcut__copy {
        display: grid;
        gap: 4px;
      }

      .jb-wishlist-search-shortcut__eyebrow {
        color: #8f5f50;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: .14em;
        line-height: 1.2;
        text-transform: uppercase;
      }

      .jb-wishlist-search-shortcut__title {
        font-size: 17px;
        font-weight: 500;
        line-height: 1.25;
      }

      .jb-wishlist-search-shortcut__description {
        color: #68615d;
        font-size: 13px;
        line-height: 1.45;
      }

      .jb-wishlist-search-shortcut__arrow {
        font-size: 19px;
        transition: transform .2s ease;
      }

      .jb-wishlist-search-shortcut:hover,
      .jb-wishlist-search-shortcut:focus-visible {
        border-color: #8f5f50;
        color: #36322f;
        outline: none;
      }

      .jb-wishlist-search-shortcut:hover .jb-wishlist-search-shortcut__arrow,
      .jb-wishlist-search-shortcut:focus-visible .jb-wishlist-search-shortcut__arrow {
        transform: translateX(3px);
      }

      @media (max-width: 749px) {
        .jb-wishlist-enhanced {
          width: min(100% - 28px, 600px);
          margin: 40px auto 72px;
        }

        .jb-wishlist-signed-out {
          width: calc(100% - 32px);
          margin: 32px auto 64px;
        }

        .jb-wishlist-auth__copy {
          padding: 38px 24px 34px;
        }

        .jb-wishlist-auth__title {
          font-size: var(--text-h1, 2rem);
        }

        .jb-wishlist-auth__description {
          margin: 18px 0 26px;
          font-size: 15px;
        }

        .jb-wishlist-auth__primary {
          width: 100%;
          min-width: 0;
        }

        .jb-wishlist-auth__find {
          margin-top: 28px;
        }

        .jb-wishlist-enhanced .head {
          font-size: 27px;
          letter-spacing: .06em;
        }

        .jb-wishlist-intro {
          margin-bottom: 32px;
          font-size: 15px;
        }

        .jb-wishlist-landing-v2 .giftreggie-landing-overview,
        .jb-wishlist-landing-v2 .giftreggie-landing-image {
          width: 100%;
          float: none;
        }

        .jb-wishlist-landing-v2 .giftreggie-landing-image {
          margin-top: 28px;
        }

        .jb-wishlist-find-v2 .gift-reggie-search-form {
          grid-template-columns: minmax(0, 1fr);
          gap: 22px;
          padding: 24px 20px;
        }

        .jb-wishlist-find-v2 .bottom-button {
          grid-column: 1;
        }

        .jb-wishlist-find-v2 .bottom-button input[type="submit"] {
          width: 100%;
          min-width: 0;
        }

        .jb-wishlist-manager-v2 .giftreggie-desktop {
          display: none !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile {
          display: grid !important;
          grid-template-columns: minmax(0, 1fr);
          gap: 14px;
        }

        .jb-wishlist-manager-v2 .giftreggie-sharing {
          flex-direction: row;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 24px;
        }

        .jb-wishlist-manager-v2 .giftreggie-share-buttons {
          display: none !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-share-buttons li,
        .jb-wishlist-copy-link,
        .jb-wishlist-print-button {
          min-height: 38px;
          padding: 0 12px;
        }

        .jb-wishlist-manager-v2 {
          margin-top: 30px;
        }

        .jb-wishlist-manager-v2 .jb-wishlist-intro {
          margin-bottom: 18px;
          font-size: 14px;
        }

        .jb-wishlist-manager-v2 .jb-wishlist-actions {
          margin-bottom: 14px;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-registry-variant {
          display: grid;
          grid-template-columns: clamp(96px, 29vw, 112px) minmax(0, 1fr);
          grid-template-areas:
            'image name'
            'image purchase';
          align-items: center;
          column-gap: 14px;
          row-gap: 10px;
          padding: 16px;
          text-align: left;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-item-image {
          grid-area: image;
          margin: 0;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-item-image img {
          width: clamp(96px, 29vw, 112px);
          height: clamp(96px, 29vw, 112px) !important;
          margin: 0;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-registry-variant > p:not(.giftreggie-item-image) {
          grid-area: name;
          align-self: end;
          margin: 0;
          padding-right: 30px;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-item-name {
          margin: 0;
          font-size: 13px;
          line-height: 1.4;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile form[action="/cart/add"] {
          grid-area: purchase;
          align-self: start;
          align-items: center;
          flex-direction: row;
          flex-wrap: nowrap;
          justify-content: flex-start;
          gap: 6px;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile form[action="/cart/add"] span {
          flex: 0 0 auto;
          margin: 0 !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-add-to-cart {
          flex: 0 0 auto;
          width: auto;
          min-width: 0;
          min-height: 38px;
          margin: 0 !important;
          padding: 0 10px;
          white-space: nowrap;
        }

        .jb-wishlist-manager-v2 .giftreggie-registry-variant-delete {
          top: 9px;
          right: 9px;
        }

        .jb-wishlist-manager-v2 .remove-button {
          width: 34px;
          min-width: 34px;
          height: 34px;
          min-height: 34px !important;
        }

        .jb-wishlist-search-shortcut {
          grid-template-columns: auto minmax(0, 1fr);
          gap: 14px;
          padding: 17px 16px;
        }

        .jb-wishlist-search-shortcut__arrow {
          display: none;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .jb-wishlist-search-shortcut__arrow,
        .jb-wishlist-auth__primary,
        .jb-wishlist-auth__find span {
          transition: none;
        }
      }

      @media (min-width: 750px) {
        .jb-wishlist-manager-v2 .giftreggie-mobile {
          display: grid !important;
        }
      }

      @media print {
        @page {
          size: letter portrait;
          margin: .45in;
        }

        html,
        body {
          background: #fff !important;
        }

        body > * {
          display: none !important;
        }

        body > main#main,
        body > main#main > .jb-wishlist-manager-v2 {
          display: block !important;
        }

        body > main#main > *:not(.jb-wishlist-manager-v2) {
          display: none !important;
        }

        body * {
          visibility: hidden !important;
        }

        .jb-wishlist-manager-v2,
        .jb-wishlist-manager-v2 * {
          visibility: visible !important;
        }

        .jb-wishlist-manager-v2 {
          position: absolute !important;
          inset: 0 auto auto 0 !important;
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          color: #000 !important;
          font-family: Arial, sans-serif !important;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }

        .jb-wishlist-manager-v2 .head {
          margin: 0 0 5pt !important;
          color: #000 !important;
          font-family: Georgia, serif !important;
          font-size: 22pt !important;
          font-weight: 400 !important;
          letter-spacing: .02em !important;
          line-height: 1.15 !important;
          text-align: left !important;
        }

        .jb-wishlist-manager-v2 .jb-wishlist-intro {
          margin: 0 0 5pt !important;
          color: #333 !important;
          font-size: 9.5pt !important;
          line-height: 1.4 !important;
          text-align: left !important;
        }

        .jb-wishlist-print-note {
          display: block !important;
          margin: 0 0 14pt !important;
          padding: 6pt 0 8pt !important;
          border-bottom: 1px solid #777 !important;
          color: #333 !important;
          font-size: 8pt !important;
          line-height: 1.35 !important;
          overflow-wrap: anywhere;
        }

        .jb-wishlist-manager-v2 .jb-wishlist-actions,
        .jb-wishlist-manager-v2 .giftreggie-sharing,
        .jb-wishlist-manager-v2 .giftreggie-desktop,
        .jb-wishlist-manager-v2 .giftreggie-registry-variant-delete,
        .jb-wishlist-manager-v2 .action-buttons,
        .jb-wishlist-manager-v2 .giftreggie-footer {
          display: none !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile {
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 10pt !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-registry-variant {
          position: relative !important;
          display: grid !important;
          grid-template-columns: 72pt minmax(0, 1fr) !important;
          grid-template-areas:
            'image name'
            'image purchase' !important;
          align-items: center !important;
          column-gap: 8pt !important;
          row-gap: 5pt !important;
          min-height: 92pt !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          border: 1px solid #aaa !important;
          padding: 8pt !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          text-align: left !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-registry-variant::before {
          content: '□';
          position: absolute;
          top: 6pt;
          right: 7pt;
          color: #444;
          font-size: 12pt;
          line-height: 1;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-item-image {
          grid-area: image !important;
          margin: 0 !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-item-image img {
          display: block !important;
          width: 72pt !important;
          height: 72pt !important;
          max-height: none !important;
          margin: 0 !important;
          object-fit: contain !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-registry-variant > p:not(.giftreggie-item-image) {
          grid-area: name !important;
          align-self: end !important;
          margin: 0 !important;
          padding-right: 14pt !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-item-name {
          margin: 0 !important;
          color: #000 !important;
          font-size: 9pt !important;
          line-height: 1.35 !important;
          text-decoration: none !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile form[action="/cart/add"] {
          grid-area: purchase !important;
          align-self: start !important;
          display: block !important;
          margin: 0 !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile form[action="/cart/add"] span {
          color: #000 !important;
          font-size: 9.5pt !important;
          font-weight: 700 !important;
        }

        .jb-wishlist-manager-v2 .giftreggie-mobile .giftreggie-add-to-cart {
          display: none !important;
        }

        .jb-wishlist-manager-v2 a[href]::after {
          content: none !important;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function insertWishlistIntro(front, text) {
    const heading = front.querySelector('.head');

    if (!heading || front.querySelector('.jb-wishlist-intro')) return;

    const intro = document.createElement('p');
    intro.className = 'jb-wishlist-intro';
    intro.textContent = text;
    heading.insertAdjacentElement('afterend', intro);
  }

  function enhanceLandingPage(front) {
    const heading = front.querySelector('.head');
    const overview = front.querySelector('.giftreggie-landing-overview');
    const image = front.querySelector('.giftreggie-landing-image img');

    front.classList.add('jb-wishlist-enhanced', 'jb-wishlist-landing-v2');

    if (heading) heading.textContent = 'Your Judith Bright Wishlist';

    insertWishlistIntro(
      front,
      'Save the pieces you love, come back anytime, and share your list when you are ready.'
    );

    if (overview) {
      overview.innerHTML = `
        <a href="${WISHLIST_ROOT}">
          <div class="giftreggie-landing-row">
            <h4>View Your Wishlist &gt;</h4>
            <p>Keep your favorite pieces together in one personal list.</p>
          </div>
        </a>
        <a href="/apps/giftregistry/find">
          <div class="giftreggie-landing-row">
            <h4>Find a Wishlist &gt;</h4>
            <p>Shop a list that someone has shared with you.</p>
          </div>
        </a>
      `;
    }

    if (image) image.alt = 'Judith Bright wishlist';
  }

  function enhanceFindWishlistPage(front) {
    front.classList.add('jb-wishlist-enhanced', 'jb-wishlist-find-v2');

    const heading = front.querySelector('.head');
    if (heading) heading.textContent = 'Find a Wishlist';

    insertWishlistIntro(
      front,
      'Enter the first and last name of the person whose wishlist you are looking for.'
    );

    const legacyIntro = front.querySelector('.giftreggie-header p:not(.jb-wishlist-intro)');
    if (legacyIntro) legacyIntro.remove();

    const footerLink = front.querySelector('.giftreggie-footer a');
    if (footerLink) footerLink.textContent = 'Create or manage a wishlist';

    const noResultsLink = [...front.querySelectorAll('.giftreggie-body a')].find((link) =>
      /no registries that match/i.test(link.textContent || '')
    );
    if (noResultsLink) noResultsLink.textContent = 'No wishlists matched your search. Search again.';

    const form = front.querySelector('.gift-reggie-search-form');

    if (!form) return;

    const fields = [
      { name: 'first-name', label: 'First name', autocomplete: 'given-name' },
      { name: 'last-name', label: 'Last name', autocomplete: 'family-name' }
    ];

    fields.forEach((field) => {
      const input = form.querySelector(`input[name="${field.name}"]`);
      const row = input ? input.closest('p') : null;

      if (!input || !row || row.classList.contains('jb-wishlist-field')) return;

      const id = `jb-wishlist-${field.name}`;
      const label = document.createElement('label');

      input.id = id;
      input.autocomplete = field.autocomplete;
      input.placeholder = field.label;
      label.htmlFor = id;
      label.textContent = field.label;

      row.className = 'jb-wishlist-field';
      row.replaceChildren(label, input);
    });

    const submit = form.querySelector('input[type="submit"]');
    if (submit) submit.value = 'Search Wishlists';

    if (!front.querySelector('.jb-wishlist-find-help')) {
      const help = document.createElement('p');
      help.className = 'jb-wishlist-find-help';
      help.textContent = 'Not finding someone? Ask them to share the link from their wishlist page.';
      form.insertAdjacentElement('afterend', help);
    }
  }

  function copyWishlistLink(input, button) {
    const markCopied = () => {
      button.textContent = 'Link Copied';
      window.setTimeout(() => {
        button.textContent = 'Copy Link';
      }, 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      markCopied();
      navigator.clipboard.writeText(input.value).catch(() => {
        input.select();
        document.execCommand('copy');
      });
      return;
    }

    input.select();
    document.execCommand('copy');
    markCopied();
  }

  function enhanceSignedOutWishlist(front) {
    const form = front.querySelector('form');
    const signupLink = front.querySelector('a[href*="/apps/giftregistry/signup"]');
    const loginLink = front.querySelector('a[href*="/apps/giftregistry/login"]');
    const message = form ? form.textContent || '' : '';

    if (!form || !/not logged in/i.test(message) || (!signupLink && !loginLink)) return false;

    const accountHref = (signupLink || loginLink).getAttribute('href');
    const panel = document.createElement('section');
    const copy = document.createElement('div');
    const eyebrow = document.createElement('p');
    const title = document.createElement('h1');
    const description = document.createElement('p');
    const primary = document.createElement('a');
    const note = document.createElement('p');
    const find = document.createElement('a');

    panel.className = 'jb-wishlist-auth';
    panel.setAttribute('aria-labelledby', 'jb-wishlist-auth-title');
    copy.className = 'jb-wishlist-auth__copy';
    eyebrow.className = 'jb-wishlist-auth__eyebrow';
    eyebrow.textContent = 'Your Judith Bright Wishlist';
    title.id = 'jb-wishlist-auth-title';
    title.className = 'jb-wishlist-auth__title';
    title.textContent = 'Save the pieces you love.';
    description.className = 'jb-wishlist-auth__description';
    description.textContent = 'Sign in or create an account to keep your favorites across devices and share your wishlist whenever you are ready.';
    primary.className = 'jb-wishlist-auth__primary';
    primary.href = accountHref;
    primary.textContent = 'Sign in or create account';
    note.className = 'jb-wishlist-auth__note';
    note.textContent = "We'll email you a secure code. No password needed.";
    find.className = 'jb-wishlist-auth__find';
    find.href = '/apps/giftregistry/find';
    find.innerHTML = 'Shopping for someone? Find their wishlist <span aria-hidden="true">&rarr;</span>';
    copy.append(eyebrow, title, description, primary, note, find);
    panel.append(copy);
    form.replaceChildren(panel);
    front.classList.add('jb-wishlist-enhanced', 'jb-wishlist-signed-out');
    return true;
  }

  function enhanceWishlistManager(front) {
    front.classList.add('jb-wishlist-enhanced', 'jb-wishlist-manager-v2');
    insertWishlistIntro(
      front,
      'The pieces you love, all in one place. Share this page whenever you are ready.'
    );

    const heading = front.querySelector('.head');

    if (heading && !front.querySelector('.jb-wishlist-actions')) {
      const actions = document.createElement('div');
      actions.className = 'jb-wishlist-actions';
      actions.innerHTML = '<a class="jb-wishlist-continue" href="/collections/all">Continue Shopping</a>';

      const intro = front.querySelector('.jb-wishlist-intro');
      (intro || heading).insertAdjacentElement('afterend', actions);
    }

    const sharing = front.querySelector('.giftreggie-sharing');
    const permalink = front.querySelector('.giftreggie-permalink');

    if (sharing && permalink && !sharing.querySelector('.jb-wishlist-copy-link')) {
      const copyButton = document.createElement('button');
      copyButton.type = 'button';
      copyButton.className = 'jb-wishlist-copy-link';
      copyButton.textContent = 'Copy Link';
      copyButton.addEventListener('click', () => copyWishlistLink(permalink, copyButton));
      sharing.prepend(copyButton);
    }

    if (sharing && !sharing.querySelector('.jb-wishlist-print-button')) {
      const printButton = document.createElement('button');
      printButton.type = 'button';
      printButton.className = 'jb-wishlist-print-button';
      printButton.textContent = 'Print Wishlist';
      printButton.addEventListener('click', () => window.print());

      const copyButton = sharing.querySelector('.jb-wishlist-copy-link');
      if (copyButton) {
        copyButton.insertAdjacentElement('afterend', printButton);
      } else {
        sharing.prepend(printButton);
      }
    }

    if (!front.querySelector('.jb-wishlist-print-note')) {
      const printNote = document.createElement('p');
      const shareUrl = permalink ? permalink.value : window.location.href;
      let cleanShareUrl = shareUrl;

      try {
        const parsedShareUrl = new URL(shareUrl, window.location.origin);
        cleanShareUrl = `${parsedShareUrl.origin}${parsedShareUrl.pathname}`;
      } catch (error) {
        // Preserve the app-provided URL if it cannot be normalized.
      }

      printNote.className = 'jb-wishlist-print-note';
      printNote.textContent = `Bring this list to a Judith Bright store and our team will help you find each piece. View online: ${cleanShareUrl}`;

      const intro = front.querySelector('.jb-wishlist-intro');
      (intro || heading || front).insertAdjacentElement('afterend', printNote);
    }
  }

  function enhanceGiftReggieExperience() {
    if (!window.location.pathname.startsWith('/apps/giftregistry')) return;

    waitForElement('.giftreggie-front', (front) => {
      injectExperienceStyles();

      if (window.location.pathname.replace(/\/$/, '') === '/apps/giftregistry') {
        enhanceLandingPage(front);
        return;
      }

      if (window.location.pathname.startsWith('/apps/giftregistry/find')) {
        enhanceFindWishlistPage(front);
        return;
      }

      if (window.location.pathname.startsWith(WISHLIST_ROOT)) {
        if (enhanceSignedOutWishlist(front)) return;
        enhanceWishlistManager(front);
      }
    });
  }

  function isWishlistSearchTerm(value) {
    return /wish\s*-?\s*list|gift\s+registr(?:y|ies)/i.test(value || '');
  }

  function buildWishlistSearchShortcut(modifier) {
    const shortcut = document.createElement('a');
    shortcut.href = '/apps/giftregistry';
    shortcut.className = `jb-wishlist-search-shortcut jb-wishlist-search-shortcut--${modifier}`;
    shortcut.setAttribute('aria-label', 'Open your Judith Bright wishlist');
    shortcut.innerHTML = `
      <span class="jb-wishlist-search-shortcut__heart" aria-hidden="true">♡</span>
      <span class="jb-wishlist-search-shortcut__copy">
        <span class="jb-wishlist-search-shortcut__eyebrow">Wishlist</span>
        <span class="jb-wishlist-search-shortcut__title">Your Judith Bright Wishlist</span>
        <span class="jb-wishlist-search-shortcut__description">View saved pieces or find a wishlist someone shared with you.</span>
      </span>
      <span class="jb-wishlist-search-shortcut__arrow" aria-hidden="true">→</span>
    `;
    return shortcut;
  }

  function enhanceWishlistSearchDiscovery() {
    const searchTerm = new URLSearchParams(window.location.search).get('q') || '';

    if (window.location.pathname === '/search' && isWishlistSearchTerm(searchTerm)) {
      waitForElement('.shopify-section--main-search', (section) => {
        injectExperienceStyles();

        if (section.querySelector('.jb-wishlist-search-shortcut--full')) return;

        const sectionStack = section.querySelector('.section-stack');
        const sectionHeader = sectionStack ? sectionStack.querySelector('.section-header') : null;
        const emptyState = section.querySelector('.empty-state');
        const shortcut = buildWishlistSearchShortcut('full');

        if (sectionHeader) {
          sectionHeader.insertAdjacentElement('afterend', shortcut);
        } else if (emptyState) {
          emptyState.prepend(shortcut);
        }
      });
    }

    waitForElement('#predictive-search-form input[name="q"]', (input) => {
      const headerSearch = input.closest('header-search');
      const predictiveSearch = headerSearch ? headerSearch.querySelector('predictive-search') : null;

      if (!headerSearch || !predictiveSearch) return;

      injectExperienceStyles();

      let shortcut = headerSearch.querySelector('.jb-wishlist-search-shortcut--predictive');

      if (!shortcut) {
        shortcut = buildWishlistSearchShortcut('predictive');
        shortcut.hidden = true;
        predictiveSearch.insertAdjacentElement('beforebegin', shortcut);
      }

      const updateShortcut = () => {
        shortcut.hidden = !isWishlistSearchTerm(input.value);
      };

      input.addEventListener('input', updateShortcut);
      input.addEventListener('search', updateShortcut);
      updateShortcut();
    });
  }

  function positionProductWishlist() {
    if (!window.location.pathname.startsWith('/products/')) return;

    injectExperienceStyles();

    const applyPosition = () => {
      const button = document.querySelector(BUTTON_SELECTOR);
      const wishlistBlock = button ? button.closest('.product-info__block-item') : null;
      const shippingNote = document.querySelector('.shipping-note');
      const shippingBlock = shippingNote ? shippingNote.closest('.product-info__block-item') : null;

      if (!button || !wishlistBlock) return;

      wishlistBlock.classList.add('jb-wishlist-product-link');

      if (
        shippingBlock &&
        wishlistBlock.parentElement === shippingBlock.parentElement &&
        shippingBlock.nextElementSibling !== wishlistBlock
      ) {
        shippingBlock.insertAdjacentElement('afterend', wishlistBlock);
      }
    };

    waitForElement(BUTTON_SELECTOR, applyPosition);

    const productRoot = document.querySelector('product-rerender') || document.body;
    let scheduled = false;

    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;

      window.requestAnimationFrame(() => {
        scheduled = false;
        applyPosition();
      });
    });

    observer.observe(productRoot, {
      childList: true,
      subtree: true
    });
  }

  function attemptPendingAdd(pending) {
    actionForPending(pending);

    if ((pending.retries || 0) >= 2) {
      waitForElement(BUTTON_SELECTOR, showRecovery);
      return;
    }

    waitForElement(BUTTON_SELECTOR, (button) => {
      if (isAdded(button)) {
        completeAddedAttempt(pending, button);
        return;
      }

      pending.stage = 'retrying';
      pending.retries = (pending.retries || 0) + 1;
      writePending(pending);

      button.dataset.jbWishlistBridgeClick = 'true';
      button.setAttribute('aria-busy', 'true');
      button.click();
      showAddingFeedback(button);
      monitorAttempt(pending);
    });
  }

  function matchingOpenWishlistButton() {
    return Array.from(document.querySelectorAll('button')).find((button) => {
      return /^\s*open wishlist\s*$/i.test(button.textContent || '');
    });
  }

  function handleWishlistPage(pending) {
    const exactRoot = window.location.pathname.replace(/\/$/, '') === WISHLIST_ROOT;
    const openedWishlist = new RegExp('^' + WISHLIST_ROOT + '/\\d+/?$').test(window.location.pathname);

    if (openedWishlist) {
      const returnUrl = new URL(pending.returnUrl, window.location.origin);
      returnUrl.searchParams.set('jb_wishlist_retry', '1');

      pending.stage = 'retrying';
      writePending(pending);
      window.location.replace(returnUrl.pathname + returnUrl.search);
      return;
    }

    if (!exactRoot) return;

    const openWishlist = () => {
      const button = matchingOpenWishlistButton();

      if (!button) return false;

      pending.stage = 'creating';
      writePending(pending);
      button.click();
      return true;
    };

    if (openWishlist()) return;

    const observer = new MutationObserver(() => {
      if (!openWishlist()) return;
      observer.disconnect();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    window.setTimeout(() => observer.disconnect(), 10000);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(BUTTON_SELECTOR);

    if (!button || !window.location.pathname.startsWith('/products/')) return;

    if (button.dataset.jbWishlistBridgeClick === 'true') {
      delete button.dataset.jbWishlistBridgeClick;
      return;
    }

    if (
      isBusy(button) ||
      hasActiveAddAttempt ||
      hasActiveRemovalAttempt ||
      hasPendingManualAdd()
    ) {
      return;
    }

    if (isAdded(button) && !isRemoveState(button)) return;

    const actionType = isRemoveState(button) ? 'remove' : 'add';
    const action = createActionRecord(actionType);

    if (actionType === 'remove') {
      monitorRemovalAttempt(action);
      return;
    }

    const pending = {
      createdAt: Date.now(),
      returnUrl: buildReturnUrl(),
      retries: 0,
      stage: 'manual',
      action
    };

    button.setAttribute('aria-busy', 'true');
    writePending(pending);
    showAddingFeedback(button);
    monitorAttempt(pending);
  }, true);

  positionProductWishlist();
  enhanceGiftReggieExperience();
  enhanceWishlistSearchDiscovery();
  window.addEventListener('online', flushKlaviyoOutbox);
  flushKlaviyoOutbox();

  const pending = readPending();

  if (!pending) return;

  if (window.location.pathname.startsWith(WISHLIST_ROOT)) {
    handleWishlistPage(pending);
    return;
  }

  if (window.location.pathname.startsWith('/products/')) {
    const returnPath = new URL(pending.returnUrl, window.location.origin).pathname;

    if (returnPath === window.location.pathname) {
      attemptPendingAdd(pending);
    }
  }
})();
